const parser = new DOMParser();
const desktop = window.matchMedia('(min-width: 990px)');

async function fetchSectionText(url, sectionId, signal) {
  const page = new URL(url, window.location.origin);
  const section = new URL(page);
  section.searchParams.set('section_id', sectionId);

  let response = await fetch(section, { signal });
  if (!response.ok) response = await fetch(page, { signal });
  if (!response.ok) throw new Error(response.statusText);
  return response.text();
}

async function fetchSection(url, sectionId, signal) {
  return parser.parseFromString(await fetchSectionText(url, sectionId, signal), 'text/html');
}

// Filter results, loaded ahead on hover/touch so the click swaps instantly.
const resultCache = new Map();
const CACHE_TTL = 60000;
const CACHE_SIZE = 40;

function cacheKey(url) {
  const target = new URL(url, window.location.origin);
  target.searchParams.delete('page');
  target.searchParams.delete('section_id');
  return `${target.pathname}${target.search}`;
}

function cachedSection(url, sectionId) {
  const key = cacheKey(url);
  const entry = resultCache.get(key);
  if (entry && Date.now() - entry.time < CACHE_TTL) return entry.promise;

  const promise = fetchSectionText(key, sectionId);
  promise.catch(() => resultCache.delete(key));
  resultCache.set(key, { promise, time: Date.now() });
  if (resultCache.size > CACHE_SIZE) resultCache.delete(resultCache.keys().next().value);
  return promise;
}

/* ---------- Category tiles (All, Clogs, Slippers…): switch collection without a reload ---------- */

const pageCache = new Map();

function fetchPage(url) {
  const key = new URL(url, window.location.origin).href;
  const entry = pageCache.get(key);
  if (entry && Date.now() - entry.time < CACHE_TTL) return entry.promise;
  const promise = fetch(key).then((response) => {
    if (!response.ok) throw new Error(response.statusText);
    return response.text();
  });
  promise.catch(() => pageCache.delete(key));
  pageCache.set(key, { promise, time: Date.now() });
  if (pageCache.size > 12) pageCache.delete(pageCache.keys().next().value);
  return promise;
}

async function swapPage(url, push = true) {
  const main = document.getElementById('MainContent');
  if (!main) return window.location.assign(url);
  const anchor = () => main.querySelector('.collection-tiles-nav, .collection-tabs');
  const before = anchor()?.getBoundingClientRect().top;
  main.classList.add('is-page-loading');
  try {
    const doc = parser.parseFromString(await fetchPage(url), 'text/html');
    const fresh = doc.getElementById('MainContent');
    if (!fresh) throw new Error('Missing main');
    main.replaceChildren(...fresh.childNodes);
    document.title = doc.title;
    // Keep the header's active menu item in step with the new collection.
    const freshLinks = [...doc.querySelectorAll('.header__menu-link')];
    document.querySelectorAll('.header__menu-link').forEach((link, index) => {
      const match = freshLinks[index];
      if (!match) return;
      link.className = match.className;
      if (match.hasAttribute('aria-current')) link.setAttribute('aria-current', match.getAttribute('aria-current'));
      else link.removeAttribute('aria-current');
    });
    if (push) window.history.pushState({}, '', url);
    const after = anchor()?.getBoundingClientRect().top;
    if (before != null && after != null) window.scrollBy(0, after - before);
  } catch {
    window.location.assign(url);
  } finally {
    main.classList.remove('is-page-loading');
  }
}

const tileFrom = (event) => event.target.closest?.('.collection-tile-link, .collection-tabs a');
['pointerover', 'touchstart', 'focusin'].forEach((type) =>
  document.addEventListener(
    type,
    (event) => {
      const tile = tileFrom(event);
      if (tile && tile.origin === window.location.origin) fetchPage(tile.href);
    },
    { passive: true }
  )
);
document.addEventListener('click', (event) => {
  const tile = tileFrom(event);
  if (!tile || tile.origin !== window.location.origin) return;
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  if (tile.getAttribute('aria-current') === 'page') return;
  swapPage(tile.href);
});

class CollectionFilters extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.form = this.querySelector('[data-filters-form]');
    if (!this.form) return;

    this.form.addEventListener('change', (event) => {
      if (event.target.matches('[data-size-local-check]')) {
        this.setLocalSize(event.target.value, event.target.checked);
        return;
      }
      if (event.target.matches('[data-price-input]')) return;
      this.submit(event.target.matches('[data-sort]') ? 0 : 120);
    });
    this.form.addEventListener('input', (event) => {
      if (event.target.matches('[data-price-input]')) this.submit(700);
    });
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.submit(0);
    });

    this.setupLocalSizes();
    this.setupPriceRanges();

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-size-local]')) {
        this.toggleLocalSize(event.target.closest('[data-size-local]').dataset.sizeLocal);
        return;
      }
      if (event.target.closest('[data-size-local-clear]')) {
        this.localSizes = [];
        this.applyLocalSizes();
        return;
      }
      const link = event.target.closest('[data-filter-link]');
      const sortOption = event.target.closest('[data-sort-value]');
      if (sortOption) {
        this.selectSort(sortOption);
      } else if (link) {
        event.preventDefault();
        // Size buttons turn black right away; the swapped bar confirms it after loading.
        if (link.hasAttribute('data-size-link')) {
          link.classList.toggle('is-active');
        }
        this.load(link.href);
      } else if (event.target.closest('[data-filters-open]')) {
        this.toggleSheet(true);
      } else if (event.target.closest('[data-filters-close]')) {
        this.toggleSheet(false);
      }
    });

    // On mobile the section headings are labels, not toggles.
    this.addEventListener('click', (event) => {
      if (!desktop.matches && event.target.closest('[data-filters-panel] .collection-filter__toggle')) event.preventDefault();
    });

    this.addEventListener(
      'toggle',
      (event) => {
        const details = event.target;
        if (!details.matches?.('[data-filter]') || !details.open || !desktop.matches) return;
        this.querySelectorAll('[data-filter][open]').forEach((other) => {
          if (other !== details) other.open = false;
        });
      },
      true
    );

    this.onDocumentClick = (event) => {
      if (!event.target.closest('.collection-sort')) {
        const sort = this.querySelector('.collection-sort[open]');
        if (sort) sort.open = false;
      }
      if (!desktop.matches || event.target.closest('[data-filter]')) return;
      this.closeDropdowns();
    };
    this.onKeydown = (event) => {
      if (event.key !== 'Escape') return;
      this.closeDropdowns();
      this.toggleSheet(false);
    };
    this.onPopState = () => {
      // Back/forward to another collection: swap the whole page, not just the grid.
      if (this.dataset.collectionPath && window.location.pathname !== this.dataset.collectionPath) {
        swapPage(window.location.href, false);
        return;
      }
      this.load(window.location.href, false);
    };

    // Start loading a filter's results as soon as the pointer or finger reaches it.
    const prefetch = (event) => {
      if (event.pointerType === 'mouse' && event.buttons) return;
      const link = event.target.closest?.('[data-filter-link]');
      if (link && this.contains(link)) {
        cachedSection(link.href, this.sectionId);
        return;
      }
      const label = event.target.closest?.('label');
      const input = label?.querySelector('input[type="checkbox"]') || (event.target.matches?.('input[type="checkbox"]') ? event.target : null);
      if (input && this.form.contains(input) && !input.disabled) cachedSection(this.urlWith(input), this.sectionId);
    };
    this.addEventListener('pointerover', prefetch);
    this.addEventListener('touchstart', prefetch, { passive: true });
    this.addEventListener('focusin', prefetch);

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('popstate', this.onPopState);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('popstate', this.onPopState);
  }

  // Collections without a Shopify size filter (e.g. Kids, option "Size"): filter the cards on the page.
  setupLocalSizes() {
    if (!this.querySelector('[data-size-local]')) return;
    const fromUrl = new URL(window.location.href).searchParams.get('size');
    this.localSizes = fromUrl ? fromUrl.split(',').filter(Boolean) : [];
    this.applyLocalSizes(false);
  }

  toggleLocalSize(size) {
    this.setLocalSize(size, !(this.localSizes || []).includes(size));
  }

  setLocalSize(size, on) {
    const sizes = (this.localSizes || []).filter((s) => s !== size);
    this.localSizes = on ? [...sizes, size] : sizes;
    this.applyLocalSizes();
  }

  applyLocalSizes(updateUrl = true) {
    const sizes = this.localSizes || [];
    this.querySelectorAll('[data-size-local]').forEach((button) => {
      const active = sizes.includes(button.dataset.sizeLocal);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });

    const items = [...this.querySelectorAll('[data-collection-grid] > li')];
    let shown = 0;
    let total = 0;
    items.forEach((item) => {
      if (!item.hasAttribute('data-item-sizes')) {
        // Promo tiles only show when nothing is filtered.
        item.hidden = sizes.length > 0;
        return;
      }
      total += 1;
      const match = !sizes.length || sizes.some((size) => item.dataset.itemSizes.includes(`|${size}|`));
      item.hidden = !match;
      if (match) shown += 1;
    });

    // Size dropdown: checkboxes, the badge and how many styles come in each size.
    this.querySelectorAll('[data-size-local-check]').forEach((box) => (box.checked = sizes.includes(box.value)));
    const meta = this.querySelector('[data-size-local-meta]');
    if (meta) meta.textContent = sizes.length ? meta.dataset.template.replace('__VALUES__', sizes.join(', ')) : meta.dataset.any;
    const badge = this.querySelector('[data-size-local-count]');
    if (badge) {
      badge.hidden = !sizes.length;
      badge.textContent = sizes.length;
    }
    this.querySelectorAll('[data-size-local-num]').forEach((num) => {
      const size = num.dataset.sizeLocalNum;
      num.textContent = items.filter((item) => item.dataset.itemSizes?.includes(`|${size}|`)).length;
    });

    const count = this.querySelector('[data-products-count]');
    if (count?.dataset.other) {
      if (!count.dataset.original) count.dataset.original = count.textContent.trim();
      count.textContent = sizes.length
        ? (shown === 1 ? count.dataset.one : count.dataset.other).replace('__COUNT__', shown)
        : count.dataset.original;
    }

    const status = this.querySelector('[data-size-local-status]');
    const text = this.querySelector('[data-size-local-text]');
    if (status && text) {
      status.hidden = !sizes.length;
      text.textContent = text.dataset.template
        .replace('__COUNT__', shown)
        .replace('__TOTAL__', total)
        .replace('__SIZE__', sizes.join(', '));
    }

    if (updateUrl) {
      const url = new URL(window.location.href);
      if (sizes.length) url.searchParams.set('size', sizes.join(','));
      else url.searchParams.delete('size');
      window.history.replaceState({}, '', url);
    }
  }

  get panel() {
    return this.querySelector('[data-filters-panel]');
  }

  selectSort(option) {
    const sort = option.closest('.collection-sort');
    const input = sort.querySelector('[data-sort]');
    sort.querySelectorAll('[data-sort-value]').forEach((item) => {
      item.setAttribute('aria-selected', String(item === option));
    });
    sort.querySelector('[data-sort-label]').textContent = option.textContent.trim();
    sort.open = false;
    sort.querySelector('summary').focus({ preventScroll: true });
    if (input.value === option.dataset.sortValue) return;
    input.value = option.dataset.sortValue;
    this.submit(0);
  }

  closeDropdowns() {
    const sort = this.querySelector('.collection-sort[open]');
    if (sort) sort.open = false;
    if (!desktop.matches) return;
    this.querySelectorAll('[data-filter][open]').forEach((details) => (details.open = false));
  }

  // Price as a two-thumb slider (mobile), driving the hidden From/To fields the form submits.
  setupPriceRanges() {
    this.querySelectorAll('.collection-filter__price').forEach((box) => {
      if (box.querySelector('[data-price-range]')) return;
      const [minInput, maxInput] = box.querySelectorAll('input[type="number"][data-price-input]');
      if (!minInput || !maxInput) return;
      const top = Number(box.dataset.high) || Math.ceil(parseFloat(maxInput.max || maxInput.placeholder) || 0);
      const bottom = Math.min(Number(box.dataset.low) || 0, top);
      if (!top) return;

      const currency = box.dataset.currency || '';
      const range = document.createElement('div');
      range.className = 'collection-filter__range';
      range.dataset.priceRange = '';
      const labels = [...box.querySelectorAll('.collection-filter__field > span')].map((span) => span.textContent.trim());
      range.innerHTML =
        `<input type="range" min="${bottom}" max="${top}" step="1" data-price-input aria-label="${labels[0] || ''}">` +
        `<input type="range" min="${bottom}" max="${top}" step="1" data-price-input aria-label="${labels[1] || ''}">` +
        '<p class="collection-filter__range-values" aria-hidden="true"></p>';
      box.append(range);

      const [low, high] = range.querySelectorAll('input');
      const values = range.querySelector('.collection-filter__range-values');
      low.value = Math.max(bottom, Math.floor(parseFloat(minInput.value) || bottom));
      high.value = Math.ceil(parseFloat(maxInput.value) || top);

      const paint = () => {
        const span = top - bottom || 1;
        range.style.setProperty('--low', `${((low.value - bottom) / span) * 100}%`);
        range.style.setProperty('--high', `${((high.value - bottom) / span) * 100}%`);
        values.textContent = `${currency}${low.value} – ${currency}${high.value}`;
        // The section line on the right shows the range, like the design ("€39 – €65").
        const meta = box.closest('[data-filter]')?.querySelector('.collection-filter__meta');
        if (meta) meta.textContent = values.textContent;
      };
      const sync = (moved) => {
        if (Number(low.value) > Number(high.value)) {
          if (moved === low) low.value = high.value;
          else high.value = low.value;
        }
        minInput.value = Number(low.value) > bottom ? low.value : '';
        maxInput.value = Number(high.value) < top ? high.value : '';
        paint();
      };
      low.addEventListener('input', () => sync(low));
      high.addEventListener('input', () => sync(high));
      paint();
    });
  }

  // Mobile sheet: every filter is shown open, like the design (no accordions).
  openAllOnMobile() {
    if (desktop.matches) return;
    this.querySelectorAll('[data-filters-panel] [data-filter]').forEach((details) => (details.open = true));
  }

  toggleSheet(open) {
    if (!this.panel) return;
    if (open) this.openAllOnMobile();
    this.panel.classList.toggle('is-open', open);
    document.documentElement.classList.toggle('filters-sheet-open', open && !desktop.matches);
    if (open) this.panel.querySelector('[data-filters-close]')?.focus({ preventScroll: true });
    else if (!desktop.matches) this.querySelector('[data-filters-open]')?.focus({ preventScroll: true });
  }

  formUrl(data = new FormData(this.form)) {
    const params = new URLSearchParams();
    for (const [key, value] of data) {
      if (value !== '') params.append(key, value);
    }
    const base = new URL(this.form.action, window.location.origin).pathname;
    return `${base}?${params}`;
  }

  // The URL the form would have if this checkbox were toggled.
  urlWith(input) {
    const data = new FormData(this.form);
    if (input.checked) {
      const kept = data.getAll(input.name).filter((value) => value !== input.value);
      data.delete(input.name);
      kept.forEach((value) => data.append(input.name, value));
    } else {
      data.append(input.name, input.value);
    }
    return this.formUrl(data);
  }

  submit(delay) {
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.load(this.formUrl()), delay);
  }

  async load(url, push = true) {
    const target = new URL(url, window.location.origin);
    target.searchParams.delete('page');
    target.searchParams.delete('section_id');

    const request = (this.request = (this.request || 0) + 1);
    this.classList.add('is-loading');

    try {
      const text = await cachedSection(target, this.sectionId);
      if (request !== this.request) return;
      const next = parser.parseFromString(text, 'text/html').querySelector('collection-filters');
      if (!next) throw new Error('Missing section');
      this.swap(next);
      if (push) window.history.pushState({}, '', `${target.pathname}${target.search}`);
    } catch (error) {
      if (request === this.request) window.location.assign(target);
    } finally {
      if (request === this.request) this.classList.remove('is-loading');
    }
  }

  swap(next) {
    const openFilters = [...this.querySelectorAll('[data-filter][open]')].map((details) => details.dataset.filter);
    const active = document.activeElement;
    const focusName = this.contains(active) && active.name ? active.name : null;

    [
      '[data-size-bar]',
      '[data-filter-groups]',
      '[data-collection-results]',
      '[data-collection-active]',
      '[data-collection-summary]',
      '[data-products-count]',
      '[data-results-label]',
      '[data-active-count]',
    ].forEach((selector) => {
      const current = this.querySelector(selector);
      const replacement = next.querySelector(selector);
      if (current && replacement) current.replaceWith(replacement);
    });
    this.classList.add('is-swapped');
    this.openAllOnMobile();
    this.setupPriceRanges();
    if (this.querySelector('[data-size-local]')) this.applyLocalSizes(false);

    const sort = next.querySelector('[data-sort]');
    if (sort) this.querySelector('[data-sort]').value = sort.value;
    const sortLabel = next.querySelector('[data-sort-label]');
    if (sortLabel) this.querySelector('[data-sort-label]').textContent = sortLabel.textContent;

    openFilters.forEach((key) => {
      const details = this.querySelector(`[data-filter="${CSS.escape(key)}"]`);
      if (details) details.open = true;
    });

    if (focusName) {
      const field = this.querySelector(`[name="${CSS.escape(focusName)}"]`);
      field?.focus({ preventScroll: true });
      if (field?.type === 'number') {
        const length = field.value.length;
        try {
          field.setSelectionRange(length, length);
        } catch {}
      }
    }
  }
}

if (!customElements.get('collection-filters')) customElements.define('collection-filters', CollectionFilters);

class CollectionLoader extends HTMLElement {
  connectedCallback() {
    this.classList.add('is-ready');
    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) this.load();
      },
      { rootMargin: '0px 0px 800px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  async load() {
    if (this.loading) return;
    this.loading = true;

    const section = this.closest('collection-filters');
    const grid = section?.querySelector('[data-collection-grid]');
    if (!section || !grid) return;

    try {
      const html = await fetchSection(this.dataset.nextUrl, section.dataset.sectionId);
      // In-stock pairs go before the sold-out ones already loaded; sold-out ones stay last.
      const firstSoldOut = grid.querySelector(':scope > li[data-sold-out]');
      html.querySelectorAll('[data-collection-grid] > li').forEach((item) => {
        if (item.hasAttribute('data-sold-out') || !firstSoldOut) grid.append(item);
        else grid.insertBefore(item, firstSoldOut);
      });
      if (section.querySelector('[data-size-local]')) section.applyLocalSizes(false);
      const nextLoader = html.querySelector('collection-loader');
      if (nextLoader) this.replaceWith(nextLoader);
      else this.remove();
    } catch {
      this.loading = false;
      this.classList.remove('is-ready');
    }
  }
}

if (!customElements.get('collection-loader')) customElements.define('collection-loader', CollectionLoader);

/* ---------- Full-width promo tiles never leave a gap ---------- */

// A promo tile spans the whole row on phones (two columns), and on wider screens wherever it is
// wider than the space left in its row. When the products before it don't fill their row, the
// row ends with an empty spot. Move the next product(s) up in front of the promo to fill it.
function fillPromoGaps(grid) {
  if (!grid) return;
  const columns = getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length;
  if (columns < 2) return;
  const visible = (item) => !item.hidden && item.offsetParent !== null;
  // How many columns an item takes, measured from its width (the computed grid-column reads 'auto').
  const gap = parseFloat(getComputedStyle(grid).columnGap) || 0;
  const column = (grid.clientWidth - gap * (columns - 1)) / columns;
  const span = (item) =>
    Math.min(Math.max(1, Math.round((item.getBoundingClientRect().width + gap) / (column + gap))), columns);
  let used = 0;
  for (let item = grid.firstElementChild; item; item = item.nextElementSibling) {
    if (!visible(item)) continue;
    const width = span(item);
    const isPromo = item.classList.contains('collection-promo');
    if (isPromo && used > 0 && used + width > columns) {
      // Pull in products that come after the promo until the row is full.
      let missing = columns - used;
      let next = item.nextElementSibling;
      while (missing > 0 && next) {
        const after = next.nextElementSibling;
        if (visible(next) && !next.classList.contains('collection-promo') && !next.hasAttribute('data-sold-out')) {
          grid.insertBefore(next, item);
          missing -= 1;
          used += 1;
        }
        next = after;
      }
    }
    used = (used + span(item)) % columns;
  }
}

function watchPromoGaps() {
  document.querySelectorAll('[data-collection-grid]').forEach((grid) => {
    if (grid.dataset.promoWatch) return;
    grid.dataset.promoWatch = '';
    let frame = 0;
    const observer = new MutationObserver(() => run());
    const observe = () => observer.observe(grid, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
    // Our own moves would re-trigger the observer: pause it while reordering.
    const run = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        observer.disconnect();
        fillPromoGaps(grid);
        observe();
      });
    };
    observe();
    run();
    window.addEventListener('resize', run, { passive: true });
  });
}

watchPromoGaps();
// Filters replace the whole grid: hook the new one when it arrives.
document.querySelectorAll('collection-filters').forEach((section) =>
  new MutationObserver(watchPromoGaps).observe(section, { childList: true, subtree: true })
);
